/**
 * Network & delivery — honest transport transparency (NET-REL-002/003/006).
 * Live per-relay state from the RelayPool (backoff, latency, acks, auth), the
 * delivery ladder, user relays and the switches that actually gate transport.
 */

import { useCallback, useEffect, useState } from "react";
import {
  composeEffectiveRelays,
  normalizeRelayKey,
  probeRelays,
  resolveSupabaseConfig,
  type PoolRelayStatus,
  type RelayPool,
  type RelayProbeResult,
} from "@veto/transport";
import { loadNetPrefs, saveNetPrefs, applyNetPrefs, type NetPrefs } from "../netPrefs";
import { getSupabaseTransport, poolRelays } from "../messaging/fallbackTransport";

type Tone = "good" | "warn" | "bad" | "idle";

export function NetworkPanel({
  relayStatus: _relayStatus,
  relayClient,
  onToast,
}: {
  /** Kept for panel-API compatibility; per-relay rows carry live status. */
  relayStatus: string;
  relayClient?: RelayPool | null;
  onToast: (msg: string) => void;
}) {
  const [prefs, setPrefs] = useState<NetPrefs>(() => loadNetPrefs());
  const [rows, setRows] = useState<PoolRelayStatus[]>([]);
  const [probes, setProbes] = useState<Map<string, RelayProbeResult>>(new Map());
  const [testing, setTesting] = useState(false);
  const [addUrl, setAddUrl] = useState("");
  const [realtimeUp, setRealtimeUp] = useState(false);

  const refreshRows = useCallback(() => {
    setRows(relayClient?.statuses() ?? []);
    setRealtimeUp(!!getSupabaseTransport()?.isUsable());
  }, [relayClient]);

  useEffect(() => {
    refreshRows();
    const t = window.setInterval(refreshRows, 2_000);
    return () => window.clearInterval(t);
  }, [refreshRows]);

  function persist(next: NetPrefs) {
    setPrefs(next);
    saveNetPrefs(next);
    applyNetPrefs(next);
  }

  async function testRelays() {
    setTesting(true);
    setProbes(new Map());
    const urls = poolRelays(prefs).map((r) => r.url);
    const results = await probeRelays(urls, {
      concurrency: 6,
      onProgress: (p) => {
        if (p.kind === "result") setProbes((m) => new Map(m).set(normalizeRelayKey(p.url), p.result));
      },
    });
    setTesting(false);
    const up = results.filter((r) => r.ok).length;
    onToast(up === 0 ? "No relay reachable right now — messages stay queued and retry automatically" : `${up}/${results.length} relays reachable`);
  }

  function reconnectNow() {
    relayClient?.nudge();
    void getSupabaseTransport()?.connect();
    onToast("Reconnecting to every relay now");
    window.setTimeout(refreshRows, 1500);
  }

  function addCustomRelay() {
    const url = addUrl.trim().replace(/\/+$/, "");
    const https = window.location.protocol === "https:";
    if (!/^wss?:\/\//i.test(url)) {
      onToast("Enter a relay URL starting with wss://");
      return;
    }
    if (https && !/^wss:\/\//i.test(url)) {
      onToast("Secure pages require wss:// relays");
      return;
    }
    if (prefs.customRelays.some((u) => normalizeRelayKey(u) === normalizeRelayKey(url))) {
      onToast("Relay already added");
      return;
    }
    persist({ ...prefs, customRelays: [...prefs.customRelays, url].slice(0, 16) });
    setAddUrl("");
    onToast("Relay added — it joins your delivery pool now");
  }

  function toggleBootstrapRelay(url: string) {
    const key = normalizeRelayKey(url);
    persist({
      ...prefs,
      disabledRelays: prefs.disabledRelays.includes(key)
        ? prefs.disabledRelays.filter((u) => u !== key)
        : [...prefs.disabledRelays, key],
    });
  }

  function restoreDefaults() {
    persist({ ...prefs, publicMesh: true, customRelays: [], autoRelays: [], disabledRelays: [] });
    onToast("Relay configuration restored to defaults");
  }

  const byKey = new Map(rows.map((r) => [normalizeRelayKey(r.url), r]));
  const operated = composeEffectiveRelays().filter((r) => r.tier !== "community");
  const customKeys = new Set(prefs.customRelays.map(normalizeRelayKey));
  const bootstrapOperated = operated.filter((r) => !customKeys.has(normalizeRelayKey(r.url)));
  const privateReady = rows.filter((r) => r.tier === "private" && r.connected).length;
  const communityRows = rows.filter((r) => r.tier === "community");
  const communityReady = communityRows.filter((r) => r.connected).length;
  const supabaseCfg = resolveSupabaseConfig();
  const totalReady = privateReady + communityReady;

  const layers: { name: string; detail: string; status: string; tone: Tone }[] = [
    {
      name: "Direct (peer-to-peer)",
      detail:
        "Opened automatically only with contacts whose safety number you verified — a direct link shows each of you the other's network address.",
      status: prefs.peerMesh ? "Verified contacts" : "Off",
      tone: prefs.peerMesh ? "good" : "idle",
    },
    {
      name: "Operated relays",
      detail: "Relays run for this deployment. Every message is sealed before it leaves your device.",
      status: operated.length === 0 ? "None configured" : `${privateReady}/${operated.length} connected`,
      tone: privateReady > 0 ? "good" : operated.length ? "bad" : "idle",
    },
    {
      name: "Public relays",
      detail:
        "A curated pool of independent Nostr relays. They see only a sealed blob, the recipient's inbox tag and the time — not who sent it or what it says.",
      status: !prefs.publicMesh ? "Off" : `${communityReady}/${communityRows.length} connected`,
      tone: !prefs.publicMesh ? "idle" : communityReady >= 2 ? "good" : communityReady === 1 ? "warn" : "bad",
    },
    {
      name: "Emergency fallback",
      detail: "Serverless last resort, used only when no relay confirms a message.",
      status: !supabaseCfg ? "Not configured" : realtimeUp ? "Standing by" : "Offline",
      tone: !supabaseCfg ? "idle" : realtimeUp ? "good" : "warn",
    },
  ];

  return (
    <div className="stack">
      <h3>Network &amp; delivery</h3>
      <p className="muted settings-lead">
        {totalReady >= 2
          ? `Healthy — connected to ${totalReady} relays. Messages are confirmed by at least two before showing ✓✓.`
          : totalReady === 1
            ? "Limited — one relay connected. Messages still send; more relays are being retried."
            : "Offline — messages are kept safely on this device and send automatically when a relay is back."}
      </p>

      <section className="setting-group">
        <h4>How messages travel</h4>
        <div className="setting-group-body">
          <ul className="transport-layers">
            {layers.map((l) => (
              <li key={l.name}>
                <div className="transport-layer-head">
                  <span className={`transport-dot dot-${l.tone}`} aria-hidden />
                  <strong>{l.name}</strong>
                  <span className={`muted text-sm tone-${l.tone}`}>{l.status}</span>
                </div>
                <span className="muted text-sm">{l.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="setting-group">
        <h4>Relays</h4>
        <div className="setting-group-body">
          <div className="relay-actions-row">
            <button type="button" className="btn small" onClick={() => void testRelays()} disabled={testing}>
              {testing ? "Testing…" : "Test relays"}
            </button>
            <button type="button" className="btn small" onClick={reconnectNow}>
              Reconnect now
            </button>
            <button type="button" className="btn small ghost" onClick={restoreDefaults}>
              Reset
            </button>
          </div>
          <ul className="relay-list relay-managed">
            {bootstrapOperated.map((r) => (
              <RelayRow
                key={r.url}
                url={r.url}
                badge="operated"
                row={byKey.get(normalizeRelayKey(r.url))}
                probe={probes.get(normalizeRelayKey(r.url))}
                disabled={prefs.disabledRelays.includes(normalizeRelayKey(r.url))}
                onRemove={() => toggleBootstrapRelay(r.url)}
              />
            ))}
            {prefs.customRelays.map((u) => (
              <RelayRow
                key={u}
                url={u}
                badge="custom"
                row={byKey.get(normalizeRelayKey(u))}
                probe={probes.get(normalizeRelayKey(u))}
                onRemove={() => persist({ ...prefs, customRelays: prefs.customRelays.filter((x) => x !== u) })}
              />
            ))}
            {prefs.publicMesh &&
              communityRows.map((r) => (
                <RelayRow key={r.url} url={r.url} badge="public" row={r} probe={probes.get(normalizeRelayKey(r.url))} />
              ))}
          </ul>
          <div className="relay-add-row">
            <input
              type="text"
              value={addUrl}
              onChange={(e) => setAddUrl(e.target.value)}
              placeholder="wss://my-relay.example"
              aria-label="Add relay URL"
            />
            <button type="button" className="btn small" onClick={addCustomRelay}>
              Add relay
            </button>
          </div>
          <p className="muted text-sm setting-group-lead">
            Your contacts&apos; apps and yours independently pick the same relays for each inbox, so
            you meet even when some relays are down. Unreachable relays are retried with growing
            pauses; queued messages flush as soon as any relay answers.
          </p>
        </div>
      </section>

      <section className="setting-group">
        <h4>Options</h4>
        <div className="setting-group-body">
          <label className="setting-row" htmlFor="net-public-mesh">
            <span className="setting-row-text">
              <span className="setting-row-title">Use public relays</span>
              <span className="setting-row-desc">
                Recommended. Turning this off leaves only operated relays — if they are down, nothing is
                delivered.
              </span>
            </span>
            <input
              id="net-public-mesh"
              type="checkbox"
              className="switch"
              checked={prefs.publicMesh}
              onChange={(e) => {
                if (!e.target.checked && operated.length === 0) {
                  onToast("No operated relays are configured — public relays must stay on");
                  return;
                }
                persist({ ...prefs, publicMesh: e.target.checked });
              }}
            />
          </label>
          <label className="setting-row" htmlFor="net-peer-mesh">
            <span className="setting-row-text">
              <span className="setting-row-title">Direct connections with verified contacts</span>
              <span className="setting-row-desc">
                Faster delivery while both apps are open. Reveals your network address to that contact.
              </span>
            </span>
            <input
              id="net-peer-mesh"
              type="checkbox"
              className="switch"
              checked={prefs.peerMesh}
              onChange={(e) => persist({ ...prefs, peerMesh: e.target.checked })}
            />
          </label>
        </div>
      </section>
    </div>
  );
}

function RelayRow({
  url,
  badge,
  row,
  probe,
  disabled,
  onRemove,
}: {
  url: string;
  badge: "operated" | "custom" | "public";
  row?: PoolRelayStatus;
  probe?: RelayProbeResult;
  disabled?: boolean;
  onRemove?: () => void;
}) {
  const retryIn = row?.nextAttemptAt ? Math.max(0, Math.round((row.nextAttemptAt - Date.now()) / 1000)) : null;
  const state = disabled
    ? "disabled"
    : row?.connected
      ? "connected"
      : row?.state === "connecting"
        ? "connecting"
        : probe?.ok
          ? "reachable"
          : row?.state === "backoff"
            ? `retry in ${retryIn}s`
            : probe
              ? "down"
              : "—";
  const tone = state === "connected" ? "ok" : state === "connecting" || state === "reachable" ? "warn" : state === "—" || state === "disabled" ? "muted" : "down";
  const latency = row?.connected ? row.latencyMs : probe?.ok ? probe.latencyMs : null;
  return (
    <li className={disabled ? "relay-row disabled" : "relay-row"}>
      <code>{url.replace(/^wss:\/\//, "")}</code>
      <span className={`relay-tier tier-${badge}`}>{badge}</span>
      {row?.authed && (
        <span className="relay-tier tier-auth" title="Signed in with your inbox key (NIP-42), so this relay serves your sealed messages">
          auth
        </span>
      )}
      <span className={`relay-state ${tone}`} title={row?.lastError ?? undefined}>
        {state}
        {latency != null ? ` · ${latency}ms` : ""}
        {row && row.publishOk + row.publishFail > 0 ? ` · ${row.publishOk}✓` : ""}
      </span>
      {onRemove && (
        <button
          type="button"
          className="relay-remove"
          onClick={onRemove}
          aria-label={`${disabled ? "Re-enable" : "Remove"} ${url}`}
          title={disabled ? "Re-enable relay" : "Remove relay"}
        >
          {disabled ? "↺" : "×"}
        </button>
      )}
    </li>
  );
}
